/**
 * FAQ pages (partials/faq, partials/faq-search): live search with highlighting and
 * snippets, scrollspy for tiles + sidebar, aria-expanded sync with the accordion
 * block JS, guilloche alignment of the tiles and the to-top button.
 */

const normalize = (text) => text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ß/g, 'ss');
const escapeHtml = (text) => { const div = document.createElement('div'); div.textContent = text; return div.innerHTML; };
const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const mark = (text, terms) => escapeHtml(text).replace(new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi'), '<mark>$1</mark>');

function initSearch(search, scope) {
  const input = search.querySelector('.faq-search__input');
  const clearButton = search.querySelector('.faq-search__clear');
  const status = search.parentNode.querySelector('.faq-search__status');
  if (!input || !clearButton || !status) return;

  // the standalone search ignores accordions that belong to a full FAQ shell
  const inShell = !!search.closest('[data-faq]');
  const items = [...scope.querySelectorAll('.accordion')]
    .filter((accordion) => inShell || !accordion.closest('[data-faq]'))
    .map((accordion) => {
      const questionEl = accordion.querySelector('.accordion-trigger span') || accordion.querySelector('.accordion-trigger');
      const body = accordion.querySelector('.faq__answer') || accordion.querySelector('.accordion-content');
      const snippet = document.createElement('p');
      snippet.className = 'faq__snippet';
      accordion.querySelector('.accordion-content')?.before(snippet);
      return {
        accordion,
        questionEl,
        plain: questionEl ? questionEl.childElementCount === 0 : false,
        question: questionEl ? questionEl.textContent : '',
        answer: (body ? body.textContent : '').replace(/\s+/g, ' ').trim(),
        section: accordion.closest('[data-faq-section]'),
        snippet,
      };
    });
  const sections = [...scope.querySelectorAll('[data-faq-section]')];
  const links = [...scope.querySelectorAll('[data-faq-target]')];

  const reset = () => {
    items.forEach((item) => {
      item.accordion.classList.remove('is-hidden', 'has-snippet');
      if (item.plain) item.questionEl.textContent = item.question;
      item.snippet.textContent = '';
    });
    sections.forEach((section) => section.classList.remove('is-hidden'));
    links.forEach((link) => link.classList.remove('is-dimmed'));
    status.textContent = '';
  };

  const apply = (raw) => {
    const query = raw.trim();
    clearButton.hidden = query.length === 0;
    if (query.length < 2) {
      reset();
      return;
    }

    const terms = query.split(/\s+/).filter(Boolean);
    const normalizedTerms = terms.map(normalize);
    const sectionHits = {};
    let total = 0;

    items.forEach((item) => {
      const haystack = normalize(`${item.question} ${item.answer}`);
      const hit = normalizedTerms.every((term) => haystack.includes(term));
      item.accordion.classList.toggle('is-hidden', !hit);
      item.accordion.classList.remove('has-snippet');

      if (!hit) {
        if (item.plain) item.questionEl.textContent = item.question;
        item.snippet.textContent = '';
        return;
      }

      total += 1;
      const sectionId = item.section ? item.section.id : '';
      sectionHits[sectionId] = (sectionHits[sectionId] || 0) + 1;
      if (item.plain) item.questionEl.innerHTML = mark(item.question, terms);

      // snippet around the first matching term in the answer
      let position = -1;
      let found = '';
      terms.forEach((term) => {
        const index = item.answer.toLowerCase().indexOf(term.toLowerCase());
        if (index > -1 && (position < 0 || index < position)) {
          position = index;
          found = term;
        }
      });
      if (position > -1) {
        const start = Math.max(0, position - 60);
        const end = Math.min(item.answer.length, position + found.length + 80);
        const excerpt = `${start > 0 ? '… ' : ''}${item.answer.slice(start, end).trim()}${end < item.answer.length ? ' …' : ''}`;
        item.snippet.innerHTML = mark(excerpt, terms);
        item.accordion.classList.add('has-snippet');
      }
    });

    let sectionCount = 0;
    sections.forEach((section) => {
      const on = !!sectionHits[section.id];
      section.classList.toggle('is-hidden', !on);
      if (on) sectionCount += 1;
    });
    links.forEach((link) => link.classList.toggle('is-dimmed', !sectionHits[link.dataset.faqTarget]));

    if (total === 0) {
      status.innerHTML = `Keine Treffer für „${escapeHtml(query)}“. Formulierung anpassen – oder <a href="/kontakt/">Frage direkt stellen</a>.`;
    } else if (sections.length) {
      status.textContent = `${total} Treffer in ${sectionCount} ${sectionCount === 1 ? 'Kategorie' : 'Kategorien'}`;
    } else {
      status.textContent = `${total} Treffer`;
    }
  };

  let debounce;
  input.addEventListener('input', () => {
    clearTimeout(debounce);
    debounce = setTimeout(() => apply(input.value), 140);
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      input.value = '';
      apply('');
    }
  });
  clearButton.addEventListener('click', () => {
    input.value = '';
    apply('');
    input.focus();
  });
}

function initShell(shell) {
  // keep aria-expanded in sync with the accordion block JS (toggles .open)
  shell.querySelectorAll('.accordion').forEach((accordion) => {
    const trigger = accordion.querySelector('.accordion-trigger');
    if (!trigger) return;
    new MutationObserver(() => {
      trigger.setAttribute('aria-expanded', accordion.classList.contains('open') ? 'true' : 'false');
    }).observe(accordion, { attributes: true, attributeFilter: ['class'] });
  });

  // scrollspy for tiles + sidebar
  const sections = [...shell.querySelectorAll('[data-faq-section]')];
  const links = [...shell.querySelectorAll('[data-faq-target]')];
  const setActive = (id) => links.forEach((link) => link.classList.toggle('is-active', link.dataset.faqTarget === id));
  let suppressUntil = 0;
  links.forEach((link) => {
    link.addEventListener('click', () => {
      suppressUntil = Date.now() + 900;
      setActive(link.dataset.faqTarget);
    });
  });
  if ('IntersectionObserver' in window && sections.length) {
    const observer = new IntersectionObserver((entries) => {
      if (Date.now() < suppressUntil) return;
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActive(entry.target.id);
      });
    }, { rootMargin: '-30% 0px -60% 0px', threshold: 0 });
    sections.forEach((section) => observer.observe(section));
  }

  const search = shell.querySelector('[data-faq-search]');
  if (search) initSearch(search, shell);

  // guilloche pattern: phase relative to the row's left edge, so it runs through the tiles
  const nav = shell.querySelector('.faq__nav');
  const alignGuilloche = () => {
    if (!nav) return;
    const left = nav.getBoundingClientRect().left;
    nav.querySelectorAll('.faq__tile').forEach((tile) => {
      tile.style.backgroundPositionX = `${Math.round(left - tile.getBoundingClientRect().left)}px`;
    });
  };
  alignGuilloche();
  window.addEventListener('resize', alignGuilloche);

  // to-top button, flush with the right content edge
  const toTop = shell.querySelector('.faq__totop');
  if (toTop) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const align = () => {
      const edge = shell.getBoundingClientRect().right - parseFloat(getComputedStyle(shell).paddingRight || '0');
      toTop.style.right = `${Math.max(16, Math.round(document.documentElement.clientWidth - edge))}px`;
    };
    align();
    window.addEventListener('resize', align);
    window.addEventListener('scroll', () => toTop.classList.toggle('is-visible', window.scrollY > 480), { passive: true });
    toTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' }));
  }
}

document.querySelectorAll('[data-faq]').forEach(initShell);
document.querySelectorAll('.faq-search-band [data-faq-search]').forEach((search) => initSearch(search, document));
